import * as React from "react";
import { Box } from "@mui/material";
import {
  LocalizationProvider,
  DateCalendar,
  PickersDay,
  PickersDayProps,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment, { Moment } from "moment";

export type Range = { start: Moment | null; end: Moment | null };
type CustomDayProps = PickersDayProps<Moment> & { range?: Range };

function CustomDay(props: CustomDayProps) {
  const { day, range, ...other } = props;
  const start = range?.start ?? null;
  const end = range?.end ?? null;

  const isStart = !!start && day.isSame(start, "day");
  const isEnd = !!end && day.isSame(end, "day");
  const isInRange =
    !!start &&
    !!end &&
    day.isSameOrAfter(start, "day") &&
    day.isSameOrBefore(end, "day");
  const isMiddle = isInRange && !isStart && !isEnd;

  return (
    <PickersDay
      {...other}
      day={day}
      selected={isStart || isEnd} // Clear selected when value is locked by displayed month
      sx={{
        margin: 0,
        padding: "0 2px",
        boxSizing: "content-box",
        ...(isMiddle && {
          borderRadius: 0,
          bgcolor: "action.hover",
        }),
        ...(isStart && {
          bgcolor: "primary.main",
          color: "primary.contrastText",
          borderTopLeftRadius: "999px",
          borderBottomLeftRadius: "999px",
          borderTopRightRadius: end ? 0 : "999px",
          borderBottomRightRadius: end ? 0 : "999px",
          "&:hover": { bgcolor: "primary.dark" },
        }),
        ...(isEnd && {
          bgcolor: "primary.main",
          color: "primary.contrastText",
          borderTopRightRadius: "999px",
          borderBottomRightRadius: "999px",
          borderTopLeftRadius: start ? 0 : "999px",
          borderBottomLeftRadius: start ? 0 : "999px",
          "&:hover": { bgcolor: "primary.dark" },
        }),
      }}
    />
  );
}

export interface RangeCalendarProps {
  /** Controlled range value (start, end). When provided with onChange, component is controlled. */
  value?: Range;
  /** Called when user selects a date; in range mode, called after each selection (start then end). */
  onChange?: (start: Moment | null, end: Moment | null) => void;
}

/**
 * Two-month calendar for selecting a date range with visual highlight.
 * Can be used uncontrolled (internal state) or controlled via value + onChange.
 */
export default function RangeCalendar({ value, onChange }: RangeCalendarProps) {
  const [internalRange, setInternalRange] = React.useState<Range>({
    start: null,
    end: null,
  });
  const isControlled = value !== undefined;
  const range = isControlled ? value! : internalRange;
  const setRange = React.useCallback(
    (updater: (prev: Range) => Range) => {
      const next = updater(range);
      if (!isControlled) setInternalRange(next);
      onChange?.(next.start, next.end);
    },
    [isControlled, range, onChange],
  );

  // Two independent calendar months
  const [leftMonth, setLeftMonth] = React.useState<Moment>(
    moment().startOf("month"),
  );
  const [rightMonth, setRightMonth] = React.useState<Moment>(
    moment().add(1, "month").startOf("month"),
  );

  // Ensure right month is never before left month
  const normalizeMonths = React.useCallback((l: Moment, r: Moment) => {
    const L = l.clone().startOf("month");
    let R = r.clone().startOf("month");
    if (R.isBefore(L, "month")) R = L.clone(); // Or L+1 if you want at least 1 month gap
    setLeftMonth(L);
    setRightMonth(R);
  }, []);

  const handleLeftMonthChange = (m: Moment) => {
    normalizeMonths(m, rightMonth);
  };

  const handleRightMonthChange = (m: Moment) => {
    // If user moves right month before left, clamp to keep right >= left
    normalizeMonths(leftMonth, m);
  };

  const handleSelect = React.useCallback(
    (date: Moment) => {
      setRange((prev) => {
        const { start, end } = prev;

        if (!start || end) {
          return { start: date, end: null };
        }

        if (date.isBefore(start, "day")) return { start: date, end: start };
        return { start, end: date };
      });
    },
    [setRange],
  );

  // Lock displayed value to the visible month (avoids calendar jump)
  const leftLock = leftMonth.clone().date(1);
  const rightLock = rightMonth.clone().date(1);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box display="flex" gap={2}>
        <DateCalendar
          defaultCalendarMonth={leftMonth}
          value={leftLock}
          onMonthChange={handleLeftMonthChange}
          onChange={(d) => d && handleSelect(d)}
          slots={{ day: CustomDay }}
          slotProps={{ day: { range } as any }}
        />

        <DateCalendar
          defaultCalendarMonth={rightMonth}
          value={rightLock}
          onMonthChange={handleRightMonthChange}
          onChange={(d) => d && handleSelect(d)}
          slots={{ day: CustomDay }}
          slotProps={{ day: { range } as any }}
        />
      </Box>
    </LocalizationProvider>
  );
}
